export { COOKIE_NAME, ONE_YEAR_MS } from "@shared/const";

/**
 * Returns true if the given origin is a Manus-hosted domain that the OAuth
 * portal accepts as a valid redirect URI.
 */
function isManusHostedOrigin(origin: string): boolean {
  try {
    const { hostname } = new URL(origin);
    return (
      hostname.endsWith(".manus.space") ||
      hostname.endsWith(".manus.computer") ||
      hostname === "localhost" ||
      hostname === "127.0.0.1"
    );
  } catch {
    return false;
  }
}

/**
 * Login URL for the current deployment.
 * - Without Manus OAuth env → first-party `/login` (Railway / Pages cutover)
 * - With Manus OAuth env → legacy Manus portal (kept for rollback)
 */
export const getLoginUrl = (returnPath?: string) => {
  const oauthPortalUrl = import.meta.env.VITE_OAUTH_PORTAL_URL;
  const appId = import.meta.env.VITE_APP_ID;

  if (!oauthPortalUrl || !appId) {
    return returnPath
      ? `/login?returnPath=${encodeURIComponent(returnPath)}`
      : "/login";
  }

  try {
    const currentOrigin = window.location.origin;
    const manusSpaceOrigin =
      import.meta.env.VITE_MANUS_SPACE_ORIGIN || currentOrigin;
    const callbackOrigin = isManusHostedOrigin(currentOrigin)
      ? currentOrigin
      : manusSpaceOrigin;
    const isCustomDomain = !isManusHostedOrigin(currentOrigin);
    const redirectUri = `${callbackOrigin}/api/oauth/callback`;

    const metadata: Record<string, string> = {};
    if (returnPath) metadata.returnPath = returnPath;
    if (isCustomDomain) metadata.finalOrigin = currentOrigin;

    const hasMetadata = Object.keys(metadata).length > 0;
    const statePayload = hasMetadata
      ? `${redirectUri}|${JSON.stringify(metadata)}`
      : redirectUri;
    const state = btoa(statePayload);

    const url = new URL(`${oauthPortalUrl}/app-auth`);
    url.searchParams.set("appId", appId);
    url.searchParams.set("redirectUri", redirectUri);
    url.searchParams.set("state", state);
    url.searchParams.set("type", "signIn");
    return url.toString();
  } catch (e) {
    console.warn("[Auth] Failed to construct login URL:", e);
    return returnPath
      ? `/login?returnPath=${encodeURIComponent(returnPath)}`
      : "/login";
  }
};
